// @file Expanded post store
import appCan from '@@/bits/app_can'
import { getVuexStore } from '@@/bits/pinia'
import { anonymous, blankUser, isRegistered } from '@@/bits/user_model'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceUserContributorsStore } from '@@/pinia/surface_user_contributors'
import type { Cid, Comment, Id, Post, User } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { isEmpty } from 'lodash-es'
import { defineStore } from 'pinia'
import { computed } from 'vue'

interface CommentWithUser extends Comment {
  user: User
}

interface ExpandedPostWithComments extends Post {
  attachment_frame_url: string
  body_frame_url: string
  author_display_name: string
  author_avatar_url: string
  comments: CommentWithUser[]
}

// Versioning of the shape of `expanded_post_data` is tracked here https://stackoverflow.com/c/padlet/articles/1921
interface ExpandedPostData {
  posts: Array<{
    id: Id
    cid: Cid
    has_attachment: boolean
  }>
  expanded_post: ExpandedPostWithComments
}

export const useExpandedPostStore = defineStore('expandedPost', () => {
  const surfaceVuexStore = getVuexStore<RootState>()
  const surfaceUserContributorStore = useSurfaceUserContributorsStore()
  const commentsStore = useCommentsStore()

  // TODO: [to-be-migrated][post_action]
  const xExpandedPostTextPanel = computed<boolean>(() => surfaceVuexStore?.getters['postAction/xExpandedPostTextPanel'])
  const expandedPostCid = computed<Cid>(() => surfaceVuexStore?.getters['postAction/expandedPostCid'])
  const expandedPost = computed<Post | undefined>(() => surfaceVuexStore?.getters['postAction/expandedPost'])
  const expandedPostId = computed<Id | null>(() => expandedPost.value?.id ?? null)
  const expandedPostPreviousPostCid = computed<Cid>(
    () => surfaceVuexStore?.getters['postAction/expandedPostPreviousPostCid'],
  )
  const expandedPostNextPostCid = computed<Cid>(() => surfaceVuexStore?.getters['postAction/expandedPostNextPostCid'])

  const isExpandedPostPreviewPost = computed<boolean>(
    () => surfaceVuexStore?.getters['postAction/isExpandedPostPreviewPost'],
  )

  const doesExpandedPostHaveAttachment = computed<boolean>(
    () => !isEmpty(expandedPost.value?.attachment) || !isEmpty(expandedPost.value?.wish_content?.attachment_props?.url),
  )

  const expandedPostCommentsWithUser = computed<CommentWithUser[]>(() => {
    const comments = expandedPostId.value != null ? commentsStore.commentsByPostId[expandedPostId.value] ?? [] : []
    const commentsWithUser = comments.map((c) => ({
      ...c,
      user: surfaceUserContributorStore.getUserById(c.user_hashid ?? (c.user_id as number)) as User,
    }))
    return commentsWithUser
  })

  const expandedPostCommenterIds = computed(() => expandedPostCommentsWithUser.value.map((c) => c.user_id))

  const expandedPostPostsToNavigate = computed<Post[]>(
    () => surfaceVuexStore?.getters['postAction/expandedPostPostsToNavigate'],
  )

  const expandedPostData = computed<ExpandedPostData | {}>(() => {
    if (expandedPost.value == null || !appCan('nativeExpandedPostV1')) return {}

    const foundAuthor =
      expandedPost.value?.author_id != null
        ? surfaceUserContributorStore.getUserById(expandedPost.value?.author_hashid ?? expandedPost.value?.author_id)
        : null
    const author = foundAuthor ?? blankUser()

    const authorDisplayName = isRegistered(author) ? author.name ?? author.username : anonymous

    const authorAvatarUrl = author.avatar

    return {
      posts: expandedPostPostsToNavigate.value.map((p) => ({
        id: p.id,
        cid: p.cid,
        has_attachment: Boolean(p.attachment),
      })),
      expanded_post: {
        ...expandedPost.value,
        attachment_frame_url: surfaceVuexStore?.getters['postAction/expandedPostMobileFrameUrl'],
        body_frame_url: surfaceVuexStore?.getters['postAction/expandedPostMobileBodyFrameUrl'],
        author_display_name: authorDisplayName,
        author_avatar_url: authorAvatarUrl,
        comments: expandedPostCommentsWithUser.value,
      },
    }
  })

  const shouldFocusExpandedPostAddCommentInput = computed<boolean>(
    () => surfaceVuexStore?.getters['postAction/shouldFocusExpandedPostAddCommentInput'],
  )

  const expandPost = (payload: { postCid: Cid; xTextPanel?: boolean; shouldFocusInput?: boolean }): void => {
    void surfaceVuexStore?.dispatch('postAction/expandPost', payload, { root: true })
  }

  const unexpandPost = (): void => {
    void surfaceVuexStore?.dispatch('postAction/unexpandPost')
  }

  const toggleExpandedPostTextPanel = (payload?: { status: boolean }): void => {
    void surfaceVuexStore?.dispatch('postAction/toggleExpandedPostTextPanel', payload)
  }

  return {
    // Getters
    xExpandedPostTextPanel,
    expandedPostCid,
    expandedPost,
    expandedPostId,
    expandedPostPreviousPostCid,
    expandedPostNextPostCid,
    isExpandedPostPreviewPost,
    doesExpandedPostHaveAttachment,
    expandedPostPostsToNavigate,
    expandedPostData,
    expandedPostCommenterIds,
    shouldFocusExpandedPostAddCommentInput,

    // Actions
    expandPost,
    unexpandPost,
    toggleExpandedPostTextPanel,
  }
})
